import { createFileRoute, Link, useNavigate, redirect } from "@tanstack/react-router";
import { useState, useRef, useEffect, type FormEvent } from "react";
import { supabase } from "@/integrations/supabase/client";
import { TwoFactorChallenge, needsMfaChallenge } from "@/components/two-factor-challenge";
import { Eye, EyeOff } from "lucide-react";

const LOCKOUT_KEY = "pluggz.admin-lockout";
const LOCKOUT_MS = 10 * 60 * 1000;
const MAX_ATTEMPTS = 5;

export const Route = createFileRoute("/admin-login")({
  beforeLoad: async () => {
    const { data } = await supabase.auth.getSession();
    if (!data.session) return;
    const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    if (aal && aal.currentLevel === "aal1" && aal.nextLevel === "aal2") {
      // Session exists but MFA hasn't been completed — render the inline challenge.
      return;
    }
    // Already signed in — if admin, go to console; otherwise bounce home.
    const { data: role } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", data.session.user.id)
      .eq("role", "admin")
      .maybeSingle();
    if (role) throw redirect({ to: "/admin" });
    throw redirect({ to: "/" });
  },
  head: () => ({
    meta: [
      { title: "Admin sign in — Pluggz" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AdminLoginPage,
});

function AdminLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isLockedOut, setIsLockedOut] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const failedAttempts = useRef<number[]>([]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(LOCKOUT_KEY);
      if (raw) {
        const ts = parseInt(raw, 10);
        if (!isNaN(ts) && Date.now() - ts < LOCKOUT_MS) {
          setIsLockedOut(true);
          const timeout = setTimeout(() => {
            setIsLockedOut(false);
            localStorage.removeItem(LOCKOUT_KEY);
          }, LOCKOUT_MS - (Date.now() - ts));
          return () => clearTimeout(timeout);
        } else {
          localStorage.removeItem(LOCKOUT_KEY);
        }
      }
    } catch {
      // ignore localStorage errors
    }
  }, []);

  function recordFailure() {
    const now = Date.now();
    failedAttempts.current.push(now);
    failedAttempts.current = failedAttempts.current.filter((t) => now - t < LOCKOUT_MS);
    if (failedAttempts.current.length >= MAX_ATTEMPTS) {
      setIsLockedOut(true);
      try {
        localStorage.setItem(LOCKOUT_KEY, String(now));
      } catch {
        // ignore
      }
      const timeout = setTimeout(() => {
        setIsLockedOut(false);
        localStorage.removeItem(LOCKOUT_KEY);
      }, LOCKOUT_MS);
      return () => clearTimeout(timeout);
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (isLockedOut) return;
    setError(null);
    setLoading(true);
    const { data, error: signInError } = await supabase.auth.signInWithPassword({ email, password });
    if (signInError || !data.user) {
      setLoading(false);
      recordFailure();
      return setError(signInError?.message ?? "Sign-in failed");
    }

    // Verify admin role before granting access.
    const { data: role, error: roleError } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", data.user.id)
      .eq("role", "admin")
      .maybeSingle();

    if (roleError || !role) {
      await supabase.auth.signOut();
      setLoading(false);
      recordFailure();
      return setError("This account is not authorized for admin access.");
    }

    setLoading(false);
    if (await needsMfaChallenge()) {
      setMfaRequired(true);
      return;
    }
    navigate({ to: "/admin" });
  }

  return (
    <div className="relative mx-auto w-full max-w-md px-4 py-12 sm:px-6 sm:py-20">
      <div className="relative">
        <p className="editorial-eyebrow">Restricted</p>
        <h1 className="mt-2 font-serif text-3xl sm:text-4xl">
          Admin <span className="italic">sign in</span>
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Operators only. Shoppers and creators should use the{" "}
          <Link to="/login" className="link-underline text-foreground">main sign-in page</Link>.
        </p>

        {mfaRequired ? (
          <div className="mt-8 space-y-4">
            <p className="text-sm text-muted-foreground">
              Two-factor authentication is required for admin sign-in.
            </p>
            <TwoFactorChallenge
              onVerified={() => navigate({ to: "/admin" })}
              onCancel={async () => {
                await supabase.auth.signOut();
                setMfaRequired(false);
              }}
            />
          </div>
        ) : (
        <form onSubmit={submit} className="mt-8 space-y-3">
          {isLockedOut && (
            <p className="rounded bg-destructive/10 px-4 py-3 text-xs text-destructive">
              Too many failed attempts. Try again in 10 minutes.
            </p>
          )}
          <div>
            <label htmlFor="admin-email" className="mb-1 block text-xs uppercase tracking-widest text-muted-foreground">Email</label>
            <input
              id="admin-email"
              required
              type="email"
              autoComplete="email"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Admin email"
              disabled={isLockedOut}
              className="w-full border border-border bg-card/80 backdrop-blur px-4 py-3 text-base outline-none transition-colors focus:border-foreground disabled:opacity-50 sm:text-sm"
            />
          </div>
          <div>
            <label htmlFor="admin-password" className="mb-1 block text-xs uppercase tracking-widest text-muted-foreground">Password</label>
            <div className="relative">
              <input
                id="admin-password"
                required
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                disabled={isLockedOut}
                className="w-full border border-border bg-card/80 backdrop-blur px-4 py-3 pr-10 text-base outline-none transition-colors focus:border-foreground disabled:opacity-50 sm:text-sm"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          {error && <p className="text-xs text-destructive">{error}</p>}
          <button
            disabled={loading || isLockedOut}
            type="submit"
            className="w-full bg-foreground px-6 py-3 text-sm uppercase tracking-widest text-background transition-all duration-300 hover:scale-[1.01] disabled:opacity-50"
          >
            {loading ? "Verifying…" : "Sign in as admin"}
          </button>
        </form>
        )}
      </div>
    </div>
  );
}
